// ✅ AMÉLIORATION 4 : Composant gestion conflits avancée
import React, { useState } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';

const ConflictAlert = ({
  conflicts = [],
  onResolve = () => {},
  onRemoveParticipant = null,  // Callback pour retirer un participant non sauvegardé
  loading = false,
  sessionId = null,
  token = null,
  startDate = null,
  endDate = null,
  currentParticipantIds = [],  // IDs des participants actuels à exclure des remplaçants
  showSuccess = () => {},
  showError = () => {}
}) => {
  const [showModal, setShowModal] = useState(false);
  const [resolutionMode, setResolutionMode] = useState('view'); // 'view', 'replace'
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [availableReplacements, setAvailableReplacements] = useState([]);
  const [loadingReplacements, setLoadingReplacements] = useState(false);
  const [replacementSearch, setReplacementSearch] = useState('');

  console.log('🔍 ConflictAlert - Props reçus:', {
    conflicts: conflicts.length,
    sessionId,
    token: token ? '✅' : '❌',
    startDate,
    endDate,
    currentParticipantIds: currentParticipantIds,
    currentParticipantIds_length: currentParticipantIds?.length
  });

  if (!conflicts || conflicts.length === 0) {
    return null;
  }

  // ✅ Analyse des conflits par type
  const conflictAnalysis = conflicts.reduce((acc, conflict) => {
    const reasons = conflict.conflicts || [];
    reasons.forEach(reason => {
      const type = reason.reason || 'Autre';
      if (!acc[type]) {
        acc[type] = { count: 0, participants: new Set() };
      }
      acc[type].count++;
      acc[type].participants.add(conflict.user_id);
    });
    return acc;
  }, {});

  // ✅ Recherche remplaçants pour un participant en conflit
  const findReplacements = async (conflictedUserId) => {
    if (!token) {
      showError('Impossible de chercher des remplaçants : token manquant');
      return;
    }

    if (!startDate || !endDate) {
      showError('Impossible de chercher des remplaçants : dates manquantes');
      return;
    }

    setLoadingReplacements(true);
    try {
      // Construire la liste des IDs à exclure : participants actuels + tous les IDs en conflit
      const allConflictUserIds = conflicts.map(c => c.user_id);
      const allExcludeIds = [...new Set([
        ...(currentParticipantIds || []),
        ...allConflictUserIds
      ])];

      console.log('🔍 findReplacements - Données envoyées:', {
        conflicted_user_ids: [conflictedUserId],
        start_date: startDate,
        end_date: endDate,
        currentParticipantIds,
        allConflictUserIds,
        exclude_user_ids: allExcludeIds,
        exclude_count: allExcludeIds.length
      });

      const response = await fetch(API_ENDPOINTS.PRESTATION_FIND_REPLACEMENTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          conflicted_user_ids: [conflictedUserId],  // Backend attend un tableau
          start_date: startDate,
          end_date: endDate,
          same_team_only: false,
          exclude_user_ids: allExcludeIds  // Exclure participants actuels + ceux en conflit
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Backend renvoie 'data' pas 'replacements'
        setAvailableReplacements(data.data || []);
        setResolutionMode('replace');
      } else {
        const errorData = await response.json();
        showError(errorData.error || 'Erreur lors de la recherche de remplaçants');
      }
    } catch (error) {
      console.error('❌ Erreur recherche remplaçants:', error);
      showError('Erreur lors de la recherche de remplaçants');
    } finally {
      setLoadingReplacements(false);
    }
  };

  // ✅ Remplacement d'un participant
  const replaceParticipant = async (conflictedUserId, replacementUserId) => {
    if (!sessionId) {
      showError('Impossible de remplacer : ID de prestation manquant');
      return;
    }

    const url = API_ENDPOINTS.PRESTATION_REPLACE_PARTICIPANT;
    const body = {
      prestation_id: sessionId,
      old_user_id: conflictedUserId,
      new_user_id: replacementUserId,
      reason: 'Résolution automatique de conflit'
    };

    console.log('🔄 replaceParticipant - Appel API:', { url, body, token: token ? 'présent' : 'ABSENT' });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      console.log('🔄 replaceParticipant - Réponse:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      // TEMPORAIRE: Afficher la réponse brute si erreur
      if (!response.ok) {
        const text = await response.text();
        console.error('🔄 replaceParticipant - Erreur brute:', text);
        showError(`Erreur ${response.status}: ${text}`);
        return;
      }

      const data = await response.json();
      showSuccess(`Participant remplacé avec succès : ${data.data?.old_user_name || ''} → ${data.data?.new_user_name || ''}`);
      setShowModal(false);
      setResolutionMode('view');
      setSelectedConflict(null);
      setAvailableReplacements([]);
      setReplacementSearch('');
      onResolve({
        oldUserId: conflictedUserId,
        newUserId: replacementUserId,
        oldUserName: data.data?.old_user_name,
        newUserName: data.data?.new_user_name
      });
    } catch (error) {
      console.error('❌ Erreur remplacement:', error);
      showError('Erreur lors du remplacement: ' + error.message);
    }
  };

  // ✅ Rendu de la raison de conflit avec icône
  const renderConflictReason = (reason) => {
    const reasonMap = {
      'training': { icon: '📚', label: 'Formation', color: '#ff6b6b' },
      'leave': { icon: '🏖️', label: 'Congé', color: '#74b9ff' },
      'sick': { icon: '🤒', label: 'Arrêt maladie', color: '#a29bfe' },
      'meeting': { icon: '📅', label: 'Réunion', color: '#fd79a8' },
      'travel': { icon: '✈️', label: 'Déplacement', color: '#fdcb6e' },
      'unavailable': { icon: '❌', label: 'Indisponible', color: '#6c5ce7' }
    };

    const info = reasonMap[reason] || { icon: '⚠️', label: reason, color: '#636e72' };
    
    return (
      <span style={{
        background: info.color,
        color: 'white',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        {info.icon} {info.label}
      </span>
    );
  };

  // ✅ Modal de résolution des conflits
  const renderConflictModal = () => {
    if (!showModal) return null;

    return (
      <div className="pi-modal-overlay">
        <div className="pi-modal" style={{ maxWidth: '600px' }}>
          <div className="pi-modal__header">
            <h3 className="pi-modal__title">
              ⚠️ Résolution des conflits
            </h3>
            <button 
              onClick={() => setShowModal(false)}
              className="pi-modal__close"
            >
              ×
            </button>
          </div>

          {resolutionMode === 'view' && (
            <div>
              <div className="pi-modal__info">
                {conflicts.length} participants en conflit détectés. Choisissez une action pour chaque conflit.
              </div>

              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {conflicts.map(conflict => {
                  const user = conflict.user_name || `Utilisateur ${conflict.user_id}`;
                  const reasons = conflict.conflicts || [];

                  return (
                    <div key={conflict.user_id} style={{
                      border: '1px solid #fee',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '12px',
                      background: '#fff5f5'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '8px'
                      }}>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>
                            {user}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {reasons.length} conflit{reasons.length > 1 ? 's' : ''}
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {/* Bouton retirer : toujours disponible */}
                          <button
                            onClick={() => {
                              if (onRemoveParticipant) {
                                onRemoveParticipant(conflict.user_id);
                                showSuccess(`${user} retiré de la prestation`);
                                // Fermer la modale si plus de conflits
                                if (conflicts.length <= 1) {
                                  setShowModal(false);
                                }
                              }
                            }}
                            className="pi-quick-btn pi-quick-btn--danger"
                            style={{ fontSize: '12px' }}
                            title="Retirer ce participant de la prestation"
                          >
                            ✗ Retirer
                          </button>
                          {/* Bouton remplacer : toujours disponible */}
                          <button
                            onClick={() => {
                              setSelectedConflict(conflict);
                              findReplacements(conflict.user_id);
                            }}
                            disabled={loadingReplacements}
                            className="pi-quick-btn pi-quick-btn--success"
                            style={{ fontSize: '12px' }}
                          >
                            {loadingReplacements ? '⏳' : '🔄'} Remplacer
                          </button>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {reasons.map((reason, index) => (
                          <div key={index}>
                            {renderConflictReason(reason.reason)}
                          </div>
                        ))}
                      </div>

                      {reasons.length > 0 && reasons[0].details && (
                        <div style={{
                          fontSize: '11px',
                          color: '#666',
                          fontStyle: 'italic',
                          marginTop: '8px'
                        }}>
                          {reasons[0].details}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {resolutionMode === 'replace' && selectedConflict && (
            <div>
              <div className="pi-modal__info">
                Remplaçants disponibles pour <strong>{selectedConflict.user_name}</strong>
                {availableReplacements.length > 0 && (
                  <span style={{ marginLeft: '8px', color: '#666' }}>
                    ({availableReplacements.length} trouvés)
                  </span>
                )}
              </div>

              {/* Champ de recherche */}
              {availableReplacements.length > 5 && (
                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher un remplaçant..."
                    value={replacementSearch}
                    onChange={(e) => setReplacementSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}

              {availableReplacements.length === 0 ? (
                <div className="pi-empty-state">
                  <div className="pi-empty-state__icon">🔍</div>
                  <div className="pi-empty-state__title">Aucun remplaçant trouvé</div>
                  <div className="pi-empty-state__subtitle">
                    Aucun participant disponible ne correspond aux critères
                  </div>
                </div>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {availableReplacements
                    .filter(r => {
                      if (!replacementSearch.trim()) return true;
                      const search = replacementSearch.toLowerCase();
                      return (
                        r.full_name?.toLowerCase().includes(search) ||
                        r.group_name?.toLowerCase().includes(search) ||
                        r.role_name?.toLowerCase().includes(search)
                      );
                    })
                    .map(replacement => (
                    <div key={replacement.user_id} style={{
                      border: replacement.is_available ? '1px solid #d4edda' : '1px solid #fff3cd',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '8px',
                      background: replacement.is_available ? '#f8fff9' : '#fffef5',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600' }}>
                          {replacement.full_name}
                          {replacement.is_available && (
                            <span style={{ color: '#28a745', marginLeft: '8px', fontSize: '12px' }}>
                              ✅ Disponible
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {replacement.group_name || 'Sans groupe'} • {replacement.role_name}
                        </div>
                        {replacement.conflict_count > 0 && (
                          <div style={{ fontSize: '11px', color: '#856404', marginTop: '2px' }}>
                            ⚠️ {replacement.conflict_count} conflit(s) sur cette période
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => {
                          if (selectedConflict.isUnsaved) {
                            // Remplacement LOCAL pour les conflits non sauvegardés
                            if (onRemoveParticipant) {
                              onRemoveParticipant(selectedConflict.user_id);
                            }
                            // Appeler onResolve avec les données pour ajouter le remplaçant
                            onResolve({
                              oldUserId: selectedConflict.user_id,
                              newUserId: replacement.user_id,
                              oldUserName: selectedConflict.user_name,
                              newUserName: replacement.full_name,
                              isLocalReplace: true  // Marqueur pour le remplacement local
                            });
                            showSuccess(`${selectedConflict.user_name || 'Participant'} remplacé par ${replacement.full_name}`);
                            setShowModal(false);
                            setResolutionMode('view');
                            setSelectedConflict(null);
                            setAvailableReplacements([]);
                            setReplacementSearch('');
                          } else {
                            // Remplacement via API pour les conflits sauvegardés
                            replaceParticipant(selectedConflict.user_id, replacement.user_id);
                          }
                        }}
                        className="pi-quick-btn pi-quick-btn--success"
                        style={{ fontSize: '12px' }}
                      >
                        ✓ Remplacer
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: '16px' }}>
                <button
                  onClick={() => {
                    setResolutionMode('view');
                    setReplacementSearch('');
                  }}
                  className="pi-modal__btn pi-modal__btn--secondary"
                >
                  ← Retour
                </button>
              </div>
            </div>
          )}

          <div className="pi-modal__actions">
            <button
              onClick={() => {
                setShowModal(false);
                // Ouvrir la modale de résolution avancée (ConflictResolutionModal)
                onResolve();
              }}
              className="pi-modal__btn pi-modal__btn--primary"
              title="Options avancées : forcer, reprogrammer, supprimer de l'autre session..."
            >
              🔧 Résolution avancée
            </button>
            <button
              onClick={() => setShowModal(false)}
              className="pi-modal__btn pi-modal__btn--secondary"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="pi-conflict-alert pi-conflict-alert--conflicts">
        <div className="pi-conflict-alert__content">
          <div className="pi-conflict-alert__icon">⚠️</div>
          <div>
            <div className="pi-conflict-alert__title">
              {conflicts.length} participant{conflicts.length > 1 ? 's' : ''} en conflit
            </div>
            <div className="pi-conflict-alert__subtitle">
              {Object.keys(conflictAnalysis).map(type => (
                <span key={type} style={{ marginRight: '12px' }}>
                  {conflictAnalysis[type].count} {type}
                </span>
              ))}
            </div>
          </div>
        </div>
        
        <button
          onClick={() => setShowModal(true)}
          className="pi-conflict-alert__btn"
          disabled={loading}
        >
          {loading ? '⏳ Vérification...' : '🔧 Résoudre'}
        </button>
      </div>

      {renderConflictModal()}
    </>
  );
};

export default ConflictAlert;

/*
// ✅ EXTRAIT DU VRAI CODE : Barre de gestion des conflits
import React from 'react';

const ConflictAlert = ({ 
  conflicts, 
  checkingConflicts, 
  multiDayInfo, 
  activeDayTab, 
  onManageConflicts 
}) => {
  if (!conflicts.length && !checkingConflicts) {
    return null;
  }

  return (
    <div style={{
      background: conflicts.length > 0 ? 'linear-gradient(135deg, #ff6b6b, #ff5252)' : '#cce5ff',
      padding: '16px 20px',
      borderRadius: '12px',
      color: conflicts.length > 0 ? 'white' : '#0d47a1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      border: conflicts.length > 0 ? '2px solid #ff4757' : '2px solid #74b9ff'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ fontSize: '24px' }}>
          {checkingConflicts ? '🔍' : conflicts.length > 0 ? '⚠️' : '✅'}
        </div>
        <div>
          <div style={{ fontWeight: '600', fontSize: '16px' }}>
            {checkingConflicts ? 'Vérification des conflits en cours...' :
             conflicts.length > 0 ? `${conflicts.length} participants en conflit détectés` :
             'Aucun conflit détecté'}
          </div>
          {conflicts.length > 0 && (
            <div style={{ fontSize: '14px', opacity: 0.9 }}>
              {multiDayInfo.isMultiDay 
                ? `Pour le ${multiDayInfo.days[activeDayTab]?.label}`
                : 'Ces participants sont déjà inscrits à d\'autres formations sur la même période'
              }
            </div>
          )}
        </div>
      </div>
      
      {conflicts.length > 0 && (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={onManageConflicts}
            style={{
              background: 'rgba(255,255,255,0.9)',
              border: 'none',
              color: '#dc3545',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            🔧 Gérer les conflits
          </button>
        </div>
      )}
    </div>
  );
};

export default ConflictAlert;
*/